import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import EditorHelpPanel from './EditorHelpPanel';
import useGuidedTutorial from './useGuidedTutorial';
import GuidedTutorialOverlay from './GuidedTutorialOverlay';
import FacilitatorPageHeader from './FacilitatorPageHeader';
import { api } from '../../lib/api';
import randomFields from '../casestudy_editor/randomFields.json';

const DIFFICULTIES = [
  { value: 'starter', label: 'Starter' },
  { value: 'standard', label: 'Standard' },
  { value: 'advanced', label: 'Advanced' },
];

const CD_SCHEDULES = [
  { value: '', label: 'Select schedule' },
  { value: '1', label: 'Schedule 1' },
  { value: '2', label: 'Schedule 2' },
  { value: '3', label: 'Schedule 3' },
  { value: '4', label: 'Schedule 4' },
  { value: '5', label: 'Schedule 5' },
];

// Curated suggestions for the dose-unit datalist. The author can still type
// anything; the datalist just speeds up common choices and keeps the same
// canonical strings flowing through grading.
const COMMON_DOSE_UNITS = [
  'tablet', 'capsule', 'mL', 'mg', 'mcg', 'g',
  'puff', 'spray', 'drop', 'lozenge', 'patch', 'sachet',
  'unit', 'application', 'suppository', 'pessary',
];

// Verify-mode catalogue. MUST mirror server VERIFY_ERROR_TYPES exactly.
// Three severity tiers: clinical (would harm), legal (would not be
// dispensed), best_practice (safe but suboptimal - counsel / amend).
const VERIFY_ERROR_TYPES = [
  // Clinical
  { key: 'wrong_drug_for_indication', label: 'Wrong drug for the stated indication', severity: 'clinical', appliesTo: 'per_drug' },
  { key: 'dose_too_high', label: 'Dose too high for this patient', severity: 'clinical', appliesTo: 'per_drug' },
  { key: 'dose_too_low', label: 'Dose too low to be therapeutic', severity: 'clinical', appliesTo: 'per_drug' },
  { key: 'wrong_route', label: 'Wrong route of administration', severity: 'clinical', appliesTo: 'per_drug' },
  { key: 'wrong_frequency', label: 'Wrong frequency for this drug', severity: 'clinical', appliesTo: 'per_drug' },
  { key: 'wrong_duration', label: 'Duration is wrong (too long or too short)', severity: 'clinical', appliesTo: 'per_drug' },
  { key: 'wrong_quantity', label: 'Quantity does not match dose x duration', severity: 'clinical', appliesTo: 'per_drug' },
  { key: 'allergy_conflict', label: "Conflicts with the patient's documented allergy", severity: 'clinical', appliesTo: 'per_drug' },
  { key: 'interaction_with_other_rx', label: 'Interaction with another drug on this script', severity: 'clinical', appliesTo: 'per_drug' },
  { key: 'contraindicated_in_condition', label: 'Contraindicated in a condition listed in the scenario', severity: 'clinical', appliesTo: 'per_drug' },
  // Legal
  { key: 'cd_quantity_in_words_missing', label: "CD: quantity in words is missing or doesn't match figures", severity: 'legal', appliesTo: 'per_drug' },
  { key: 'cd_form_missing', label: 'CD: pharmaceutical form not specified', severity: 'legal', appliesTo: 'per_drug' },
  { key: 'cd_strength_missing', label: 'CD: strength not specified', severity: 'legal', appliesTo: 'per_drug' },
  { key: 'cd_instalment_missing', label: 'CD: instalment direction missing on instalment prescription', severity: 'legal', appliesTo: 'per_drug' },
  { key: 'prescriber_address_missing', label: 'Prescriber address not present', severity: 'legal', appliesTo: 'whole_form' },
  { key: 'patient_address_missing', label: 'Patient address not present', severity: 'legal', appliesTo: 'whole_form' },
  { key: 'signature_missing', label: 'Prescriber signature not present', severity: 'legal', appliesTo: 'whole_form' },
  { key: 'date_missing', label: 'Prescription not dated', severity: 'legal', appliesTo: 'whole_form' },
  { key: 'patient_age_missing_paediatric', label: 'Patient age not present (legal requirement under 12)', severity: 'legal', appliesTo: 'whole_form' },
  // Best practice
  { key: 'dose_suboptimal_best_practice', label: 'A different dose may be more appropriate', severity: 'best_practice', appliesTo: 'per_drug' },
  { key: 'formulation_suboptimal_best_practice', label: 'A different formulation may be more appropriate (e.g. liquid for paediatric)', severity: 'best_practice', appliesTo: 'per_drug' },
  { key: 'frequency_suboptimal_best_practice', label: 'Dose interval could be optimised', severity: 'best_practice', appliesTo: 'per_drug' },
  { key: 'duration_suboptimal_best_practice', label: 'Duration could be optimised', severity: 'best_practice', appliesTo: 'per_drug' },
  { key: 'monitoring_not_specified_best_practice', label: 'Monitoring requirement should be specified', severity: 'best_practice', appliesTo: 'per_drug' },
  { key: 'counselling_opportunity_best_practice', label: 'Patient counselling opportunity (lifestyle, adherence, side effects)', severity: 'best_practice', appliesTo: 'whole_form' },
];
const VERIFY_ERROR_BY_KEY = Object.fromEntries(VERIFY_ERROR_TYPES.map((e) => [e.key, e]));

// Verify-mode actions. MUST mirror server VERIFY_ACTIONS exactly.
const VERIFY_ACTIONS = [
  { key: 'sign_off', label: 'Sign off — no intervention needed' },
  { key: 'amend_myself', label: 'Amend prescription, sign and annotate' },
  { key: 'query_with_prescriber', label: 'Query with the prescriber' },
  { key: 'provide_advice_to_patient', label: 'Provide advice to the patient' },
  { key: 'refuse_to_dispense', label: 'Refuse to dispense' },
];

const SEVERITY_BADGE_VARIANT = {
  clinical: { bg: 'danger' },
  legal: { bg: 'warning', text: 'dark' },
  best_practice: { bg: 'info' },
};

// Same severity-derived default as the server. The lecturer can override
// in the editor; we preselect this in the dropdown.
const deriveDefaultVerifyAction = (errors) => {
  if (!Array.isArray(errors) || errors.length === 0) return 'sign_off';
  if (errors.some((e) => VERIFY_ERROR_BY_KEY[e.key]?.severity === 'clinical')) return 'refuse_to_dispense';
  if (errors.some((e) => VERIFY_ERROR_BY_KEY[e.key]?.severity === 'legal')) return 'query_with_prescriber';
  if (errors.some((e) => VERIFY_ERROR_BY_KEY[e.key]?.severity === 'best_practice')) return 'provide_advice_to_patient';
  return 'sign_off';
};

const SIMULATION_PRESCRIBER_ADDRESS = 'Auxtechna Training Hospital - SIMULATION ADDRESS - NOT A REAL CLINICIAN';

const blankVerifyDrug = () => ({
  drug: '',
  formulation: '',
  strength: '',
  route: '',
  doseAmount: '',
  doseUnit: '',
  frequency: '',
  durationDays: '',
  durationDoses: '',
  quantity: '',
  quantityInWords: '',
  isControlledDrug: false,
});

const blankVerifyError = () => ({ key: '', drug: '', note: '' });

// Match server's normaliseOutpatientDrugName: case + punctuation insensitive.
const normaliseDrugKey = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const blankAnswer = () => ({
  drug: '',
  formulation: '',
  strength: '',
  route: '',
  doseAmount: '',
  doseUnit: '',
  frequency: '',
  durationDays: '',
  durationDoses: '',
  quantity: '',
});

const blankSlot = (idx) => ({
  id: `slot-${idx + 1}`,
  label: '',
  controlledDrug: false,
  cdSchedule: '',
  instalmentScript: false,
  counsellingFeedback: '',
  lecturerReviewRequired: false,
  acceptableAnswers: [blankAnswer()],
});

const buildInitialState = (drill) => {
  const payload = drill?.payload || {};
  const mode = (drill?.mode === 'verify' || payload.mode === 'verify') ? 'verify' : 'prescribe';
  const slots = Array.isArray(payload.expectedAnswerSlots) && payload.expectedAnswerSlots.length
    ? payload.expectedAnswerSlots.map((s, idx) => ({
        id: s.id || `slot-${idx + 1}`,
        label: s.label || '',
        controlledDrug: Boolean(s.controlledDrug),
        cdSchedule: s.cdSchedule != null ? String(s.cdSchedule) : '',
        instalmentScript: Boolean(s.instalmentScript),
        counsellingFeedback: s.counsellingFeedback || '',
        lecturerReviewRequired: Boolean(s.lecturerReviewRequired),
        acceptableAnswers: Array.isArray(s.acceptableAnswers) && s.acceptableAnswers.length
          ? s.acceptableAnswers.map((a) => ({
              drug: a.drug || '',
              formulation: a.formulation || '',
              strength: a.strength || '',
              route: a.route || '',
              doseAmount: a.doseAmount != null ? String(a.doseAmount) : '',
              doseUnit: a.doseUnit || '',
              frequency: a.frequency || '',
              durationDays: a.durationDays != null ? String(a.durationDays) : '',
              durationDoses: a.durationDoses != null ? String(a.durationDoses) : '',
              quantity: a.quantity != null ? String(a.quantity) : '',
            }))
          : [blankAnswer()],
      }))
    : [blankSlot(0)];
  const vp = payload.verifyPrescription || {};
  const verifyDrugs = Array.isArray(vp.drugs) && vp.drugs.length
    ? vp.drugs.map((d) => ({
        drug: d.drug || '',
        formulation: d.formulation || '',
        strength: d.strength || '',
        route: d.route || '',
        doseAmount: d.doseAmount != null ? String(d.doseAmount) : '',
        doseUnit: d.doseUnit || '',
        frequency: d.frequency || '',
        durationDays: d.durationDays != null ? String(d.durationDays) : '',
        durationDoses: d.durationDoses != null ? String(d.durationDoses) : '',
        quantity: d.quantity != null ? String(d.quantity) : '',
        quantityInWords: d.quantityInWords || '',
        isControlledDrug: Boolean(d.isControlledDrug),
      }))
    : [blankVerifyDrug()];
  return {
    title: drill?.title || '',
    summary: drill?.summary || '',
    freeTier: Boolean(drill?.freeTier),
    difficulty: drill?.difficulty || 'standard',
    mode,
    patient: {
      name: payload.patient?.name || '',
      dob: payload.patient?.dob || '',
      trainingNhsNumber: payload.patient?.trainingNhsNumber || '',
      trainingAddress: payload.patient?.trainingAddress || 'Training Scenario - Not a Real Address',
      allergies: payload.patient?.allergies || '',
    },
    scenario: payload.scenario || '',
    // Prescribe-mode shape
    expectedAnswerSlots: slots,
    // Verify-mode shape
    verifyPrescription: {
      drugs: verifyDrugs,
      formMeta: {
        patientAddress: vp.formMeta?.patientAddress || (payload.patient?.trainingAddress || 'Training Scenario - Not a Real Address'),
        prescriberAddress: vp.formMeta?.prescriberAddress || SIMULATION_PRESCRIBER_ADDRESS,
        prescriptionDate: vp.formMeta?.prescriptionDate || '',
        signaturePresent: vp.formMeta?.signaturePresent !== false,
      },
      missing: Array.isArray(vp.missing) ? vp.missing : [],
    },
    verifyErrors: Array.isArray(payload.verifyErrors)
      ? payload.verifyErrors.map((e) => ({ key: e.key || '', drug: e.drug || '', note: e.note || '' }))
      : [],
    verifyCorrectAction: payload.correctAction || '',
    verifyLecturerReviewRequired: Boolean(payload.lecturerReviewRequired),
    customMustHaves: Array.isArray(payload.customMustHaves) ? payload.customMustHaves.join('\n') : '',
  };
};

const toTitleCase = (s) => String(s || '')
  .split(' ').filter(Boolean)
  .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
  .join(' ');

const buildRandomTrainingNhsNumber = () => `TRAINING-${Array.from({ length: 10 }, () => Math.floor(Math.random() * 10)).join('')}`;

const buildRandomDobForAge = (age) => {
  const today = new Date();
  const year = today.getFullYear() - age - Math.floor(Math.random() * 1);
  const month = Math.floor(Math.random() * 12) + 1;
  const day = Math.floor(Math.random() * 28) + 1;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const formatLibraryStrength = (item) => {
  const s = String(item?.strength || '').trim();
  const u = String(item?.unit || '').trim();
  if (!s) return '';
  if (!u) return s;
  return s.toLowerCase().includes(u.toLowerCase()) ? s : `${s} ${u}`;
};

// Parse the numeric strength out of e.g. "1.25mg", "500", "1.25 mg".
// Slash deliberately omitted so combo strengths like "100/6 micrograms"
// (Fostair) parse as just "100" + the trailing word - we ignore the unit
// anyway for non-tablet forms.
const parseStrengthNumeric = (item) => {
  const s = String(item?.strength || '').trim();
  const fallbackUnit = String(item?.unit || '').trim();
  const m = s.match(/^([\d.]+)\s*([a-zA-Zμ]+)?/);
  if (!m) return { amount: '', unit: fallbackUnit };
  return { amount: m[1], unit: (m[2] || fallbackUnit).trim() };
};

// Per-formulation dose unit. Tablet/capsule -> dose mirrors strength
// ("Bisoprolol 1.25mg" -> "1.25 mg"); inhaler/spray/etc -> 1 of the
// discrete unit ("Fostair inhaler" -> "1 puff"); liquids and topicals
// have no good default and the lecturer fills it in manually.
const FORM_TO_DOSE_UNIT = {
  inhaler: 'puff',
  spray: 'spray',
  drop: 'drop',
  drops: 'drop',
  lozenge: 'lozenge',
  patch: 'patch',
  sachet: 'sachet',
  pessary: 'pessary',
  suppository: 'suppository',
};

const defaultDoseFromItem = (item) => {
  const form = String(item?.form || '').trim().toLowerCase();
  if (form === 'tablet' || form === 'capsule') {
    const { amount, unit } = parseStrengthNumeric(item);
    if (amount) return { amount, unit };
    return { amount: '1', unit: form };
  }
  const mapped = FORM_TO_DOSE_UNIT[form];
  if (mapped) return { amount: '1', unit: mapped };
  return { amount: '', unit: '' };
};

const DrugAutocomplete = ({ value, onChange, onPick, library = [], placeholder, disabled }) => {
  const [focused, setFocused] = useState(false);
  const [hover, setHover] = useState(false);
  const containerRef = useRef(null);
  const matches = useMemo(() => {
    const q = String(value || '').trim().toLowerCase();
    if (q.length < 2) return [];
    return library
      .filter((item) => {
        const name = String(item.drugName || '').toLowerCase();
        const aliases = String(item.aliases || '').toLowerCase();
        return name.includes(q) || aliases.includes(q);
      })
      .slice(0, 8);
  }, [value, library]);
  const showDropdown = (focused || hover) && matches.length > 0 && !disabled;
  return (
    <div
      ref={containerRef}
      style={{ position: 'relative' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Form.Control
        size="sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder={placeholder || 'Type 2+ characters to search'}
        disabled={disabled}
      />
      {showDropdown ? (
        <div
          style={{
            position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0,
            backgroundColor: '#fff', border: '1px solid #ced4da', borderRadius: 4,
            maxHeight: 240, overflowY: 'auto', boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
          }}
        >
          {matches.map((item) => (
            <button
              type="button"
              key={item.id}
              onMouseDown={(e) => { e.preventDefault(); onPick(item); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '6px 10px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
              }}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f1f3f5'; }}
              onFocus={(e) => { e.currentTarget.style.backgroundColor = '#f1f3f5'; }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              onBlur={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div className="fw-semibold">
                {item.drugName} {formatLibraryStrength(item)}{item.form ? ` (${item.form})` : ''}
                {item.controlledDrug ? <Badge bg="warning" text="dark" className="ms-2">CD</Badge> : null}
              </div>
              <div className="small text-muted">
                {[item.defaultRoute, item.usualFrequencies, item.defaultIndication].filter(Boolean).join(' | ')}
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const FacilitatorOutpatientEditor = ({
  drill, loadFullDrill, onSave, onCancel, onPreview, saving = false, errorMessage,
}) => {
  const [state, setState] = useState(() => buildInitialState(drill));
  const [loading, setLoading] = useState(false);
  const [drugLibrary, setDrugLibrary] = useState([]);
  const [routeOptions, setRouteOptions] = useState([]);
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [libraryLoaded, setLibraryLoaded] = useState(false);
  const isEdit = Boolean(drill?.id);

  const basicsRef = useRef(null);
  const patientRef = useRef(null);
  const scenarioRef = useRef(null);
  const slotsRef = useRef(null);
  const saveBarRef = useRef(null);

  const tutorialSteps = useMemo(() => {
    const isVerify = state.mode === 'verify';
    return [
      { key: 'basics', title: 'Case study basics', body: 'Title, summary, difficulty, free-tier toggle, and Mode (Prescribe vs Verify).', ref: basicsRef },
      { key: 'patient', title: 'Patient details', body: 'Use the Random Male / Female buttons to fill realistic-looking demographics. The training NHS number and training address are obviously fake; the rendered prescription always carries a TRAINING SIMULATION banner so the form is not a real prescription.', ref: patientRef },
      { key: 'scenario', title: 'Clinical scenario', body: isVerify
        ? 'A short paragraph the student reads before reviewing the prescription. State what they need to know to spot the errors (recent treatment, allergies, weight, conditions).'
        : 'A short paragraph the student reads before writing the prescription. State what is being prescribed for and why.', ref: scenarioRef },
      { key: 'slots', title: isVerify ? 'Prescription to review + errors' : 'Expected prescription', body: isVerify
        ? 'Author the prescription the student will review (often deliberately flawed). Then in the "Errors in this prescription" panel, tick every error the student should flag from the closed catalogue. Per-drug errors need a target drug; whole-form errors apply to the script overall.'
        : 'Each entry is one drug the student should prescribe. Within an entry, list one or more acceptable answers - each with a library-picked drug plus dose, route and frequency selected from the same dropdowns the student uses. Tick "Controlled drug" to auto-apply CD legal requirements.', ref: slotsRef },
      { key: 'save', title: 'Save then publish', body: 'Save creates a draft. Publish from the library page when ready.', ref: saveBarRef },
    ];
  }, [state.mode]);
  const tutorial = useGuidedTutorial(tutorialSteps);

  useEffect(() => {
    if (!isEdit || drill?.payload || !loadFullDrill) return;
    let cancelled = false;
    setLoading(true);
    loadFullDrill(drill.id)
      .then((full) => { if (!cancelled) setState(buildInitialState(full?.drill || full || {})); })
      .catch(() => { /* parent surfaces */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isEdit, drill?.id, drill?.payload, loadFullDrill]);

  useEffect(() => {
    let cancelled = false;
    api.getDrugLibrary()
      .then((res) => {
        if (cancelled) return;
        setDrugLibrary(res?.items || []);
        setRouteOptions(Array.isArray(res?.metadata?.routes) ? res.metadata.routes : []);
        setFrequencyOptions(Array.isArray(res?.metadata?.frequencies) ? res.metadata.frequencies : []);
        setLibraryLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setDrugLibrary([]);
        setRouteOptions([]);
        setFrequencyOptions([]);
        setLibraryLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  const updatePatient = (k, v) => setState((s) => ({ ...s, patient: { ...s.patient, [k]: v } }));

  const generatePatient = (genderKey) => {
    const firstNames = randomFields.names[genderKey] || [];
    const surnames = randomFields.names.surnames || [];
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)] || 'Patient';
    const surname = toTitleCase(surnames[Math.floor(Math.random() * surnames.length)] || 'Example');
    const age = Math.floor(Math.random() * (85 - 18 + 1)) + 18;
    setState((s) => ({
      ...s,
      patient: {
        ...s.patient,
        name: `${firstName} ${surname}`,
        dob: buildRandomDobForAge(age),
        trainingNhsNumber: buildRandomTrainingNhsNumber(),
        trainingAddress: 'Training Scenario - Not a Real Address',
        allergies: s.patient.allergies || 'NKDA',
      },
    }));
  };

  const updateSlot = (slotIdx, k, v) => setState((s) => ({
    ...s,
    expectedAnswerSlots: s.expectedAnswerSlots.map((slot, i) => i === slotIdx ? { ...slot, [k]: v } : slot),
  }));
  const addSlot = () => setState((s) => ({
    ...s,
    expectedAnswerSlots: [...s.expectedAnswerSlots, blankSlot(s.expectedAnswerSlots.length)],
  }));
  const removeSlot = (slotIdx) => setState((s) => ({
    ...s,
    expectedAnswerSlots: s.expectedAnswerSlots.length > 1
      ? s.expectedAnswerSlots.filter((_, i) => i !== slotIdx)
      : s.expectedAnswerSlots,
  }));

  const updateAnswer = (slotIdx, ansIdx, k, v) => setState((s) => ({
    ...s,
    expectedAnswerSlots: s.expectedAnswerSlots.map((slot, i) => {
      if (i !== slotIdx) return slot;
      return {
        ...slot,
        acceptableAnswers: slot.acceptableAnswers.map((a, j) => j === ansIdx ? { ...a, [k]: v } : a),
      };
    }),
  }));
  const addAnswer = (slotIdx) => setState((s) => ({
    ...s,
    expectedAnswerSlots: s.expectedAnswerSlots.map((slot, i) => {
      if (i !== slotIdx) return slot;
      return { ...slot, acceptableAnswers: [...slot.acceptableAnswers, blankAnswer()] };
    }),
  }));
  const removeAnswer = (slotIdx, ansIdx) => setState((s) => ({
    ...s,
    expectedAnswerSlots: s.expectedAnswerSlots.map((slot, i) => {
      if (i !== slotIdx) return slot;
      return {
        ...slot,
        acceptableAnswers: slot.acceptableAnswers.length > 1
          ? slot.acceptableAnswers.filter((_, j) => j !== ansIdx)
          : slot.acceptableAnswers,
      };
    }),
  }));

  const pickDrug = (slotIdx, ansIdx, item) => {
    const strength = formatLibraryStrength(item);
    const route = item.defaultRoute || '';
    const frequency = item.usualFrequencies || '';
    const { amount: doseAmount, unit: doseUnit } = defaultDoseFromItem(item);
    setState((s) => {
      const newSlots = s.expectedAnswerSlots.map((slot, i) => {
        if (i !== slotIdx) return slot;
        const newAnswers = slot.acceptableAnswers.map((a, j) => {
          if (j !== ansIdx) return a;
          return {
            ...a,
            drug: item.drugName,
            formulation: item.form || a.formulation,
            strength: strength || a.strength,
            route: route || a.route,
            doseAmount: doseAmount || a.doseAmount,
            doseUnit: doseUnit || a.doseUnit,
            frequency: frequency || a.frequency,
          };
        });
        let newSlot = { ...slot, acceptableAnswers: newAnswers };
        if (item.controlledDrug && !slot.controlledDrug) {
          newSlot = { ...newSlot, controlledDrug: true };
        }
        return newSlot;
      });
      return { ...s, expectedAnswerSlots: newSlots };
    });
  };

  // ----- Verify-mode setters -----
  const setMode = (mode) => setState((s) => ({ ...s, mode }));
  const updateVerifyDrug = (idx, k, v) => setState((s) => ({
    ...s,
    verifyPrescription: {
      ...s.verifyPrescription,
      drugs: s.verifyPrescription.drugs.map((d, i) => i === idx ? { ...d, [k]: v } : d),
    },
  }));
  const addVerifyDrug = () => setState((s) => ({
    ...s,
    verifyPrescription: { ...s.verifyPrescription, drugs: [...s.verifyPrescription.drugs, blankVerifyDrug()] },
  }));
  const removeVerifyDrug = (idx) => setState((s) => {
    const remainingDrugs = s.verifyPrescription.drugs.length > 1
      ? s.verifyPrescription.drugs.filter((_, i) => i !== idx)
      : s.verifyPrescription.drugs;
    const removedName = (s.verifyPrescription.drugs[idx]?.drug || '').trim();
    // Drop verify-errors that pointed at the removed drug.
    const filteredErrors = removedName
      ? s.verifyErrors.filter((e) => normaliseDrugKey(e.drug) !== normaliseDrugKey(removedName))
      : s.verifyErrors;
    return {
      ...s,
      verifyPrescription: { ...s.verifyPrescription, drugs: remainingDrugs },
      verifyErrors: filteredErrors,
    };
  });

  const pickVerifyDrug = (idx, item) => {
    const strength = formatLibraryStrength(item);
    const route = item.defaultRoute || '';
    const frequency = item.usualFrequencies || '';
    const { amount: doseAmount, unit: doseUnit } = defaultDoseFromItem(item);
    setState((s) => ({
      ...s,
      verifyPrescription: {
        ...s.verifyPrescription,
        drugs: s.verifyPrescription.drugs.map((d, i) => {
          if (i !== idx) return d;
          return {
            ...d,
            drug: item.drugName,
            formulation: item.form || d.formulation,
            strength: strength || d.strength,
            route: route || d.route,
            doseAmount: doseAmount || d.doseAmount,
            doseUnit: doseUnit || d.doseUnit,
            frequency: frequency || d.frequency,
            isControlledDrug: Boolean(item.controlledDrug),
          };
        }),
      },
    }));
  };

  const updateVerifyError = (idx, k, v) => setState((s) => ({
    ...s,
    verifyErrors: s.verifyErrors.map((e, i) => i === idx ? { ...e, [k]: v } : e),
  }));
  const addVerifyError = () => setState((s) => ({
    ...s,
    verifyErrors: [...s.verifyErrors, blankVerifyError()],
  }));
  const removeVerifyError = (idx) => setState((s) => ({
    ...s,
    verifyErrors: s.verifyErrors.filter((_, i) => i !== idx),
  }));

  const validation = useMemo(() => {
    const errors = [];
    if (!state.title.trim()) errors.push('Title is required.');
    if (!state.summary.trim()) errors.push('Summary is required.');
    if (!state.patient.name.trim()) errors.push('Patient name is required.');
    if (!state.scenario.trim()) errors.push('Clinical scenario is required.');
    if (state.mode === 'verify') {
      const validVerifyDrugs = state.verifyPrescription.drugs.filter((d) => d.drug.trim());
      if (validVerifyDrugs.length === 0) {
        errors.push('Add at least one drug to the prescription the student will review.');
      }
      // Errors are optional (zero-error case is valid - student should sign off).
      // But each error that IS specified must have a valid type and, if per_drug, a target.
      state.verifyErrors.forEach((err, i) => {
        if (!err.key) {
          errors.push(`Error #${i + 1}: pick an error type from the catalogue.`);
          return;
        }
        const cat = VERIFY_ERROR_BY_KEY[err.key];
        if (!cat) {
          errors.push(`Error #${i + 1}: invalid error type.`);
          return;
        }
        if (cat.appliesTo === 'per_drug') {
          const drugName = (err.drug || '').trim();
          if (!drugName) {
            errors.push(`Error #${i + 1} (${cat.label}): pick which drug it applies to.`);
            return;
          }
          if (!validVerifyDrugs.some((d) => normaliseDrugKey(d.drug) === normaliseDrugKey(drugName))) {
            errors.push(`Error #${i + 1} (${cat.label}): "${drugName}" isn't on the prescription you've authored.`);
          }
        }
      });
    } else {
      const validSlots = state.expectedAnswerSlots
        .map((s) => ({ ...s, acceptableAnswers: s.acceptableAnswers.filter((a) => a.drug.trim()) }))
        .filter((s) => s.acceptableAnswers.length > 0);
      if (validSlots.length === 0) errors.push('Add at least one expected prescription with at least one acceptable answer.');
      validSlots.forEach((slot, i) => {
        if (slot.controlledDrug && !slot.cdSchedule) {
          errors.push(`Prescription ${i + 1} (${slot.label || 'unlabelled'}) is a CD - select a CD schedule.`);
        }
      });
    }
    return errors;
  }, [state]);

  const buildPayload = () => {
    const base = {
      mode: state.mode,
      patient: { ...state.patient },
      scenario: state.scenario,
      formType: 'fp10_outpatient',
      customMustHaves: state.customMustHaves
        .split('\n').map((m) => m.trim()).filter(Boolean),
    };
    if (state.mode === 'verify') {
      return {
        ...base,
        verifyPrescription: {
          drugs: state.verifyPrescription.drugs
            .filter((d) => d.drug.trim())
            .map((d) => ({
              drug: d.drug.trim(),
              formulation: d.formulation.trim(),
              strength: d.strength.trim(),
              route: d.route.trim(),
              doseAmount: d.doseAmount === '' ? null : Number(d.doseAmount),
              doseUnit: d.doseUnit.trim(),
              frequency: d.frequency.trim(),
              durationDays: d.durationDays === '' ? null : Number(d.durationDays),
              durationDoses: d.durationDoses === '' ? null : Number(d.durationDoses),
              quantity: d.quantity === '' ? null : Number(d.quantity),
              quantityInWords: (d.quantityInWords || '').trim(),
              isControlledDrug: Boolean(d.isControlledDrug),
            })),
          formMeta: {
            patientAddress: state.verifyPrescription.formMeta.patientAddress,
            prescriberAddress: state.verifyPrescription.formMeta.prescriberAddress,
            prescriptionDate: state.verifyPrescription.formMeta.prescriptionDate,
            signaturePresent: Boolean(state.verifyPrescription.formMeta.signaturePresent),
          },
          missing: Array.isArray(state.verifyPrescription.missing) ? state.verifyPrescription.missing : [],
        },
        verifyErrors: state.verifyErrors
          .filter((e) => e.key && VERIFY_ERROR_BY_KEY[e.key])
          .map((e) => {
            const cat = VERIFY_ERROR_BY_KEY[e.key];
            return {
              key: e.key,
              drug: cat.appliesTo === 'per_drug' ? (e.drug || '').trim() : '',
              note: (e.note || '').trim(),
            };
          }),
        // Lecturer-specified correct action wins on the server. Empty string
        // -> server falls back to severity-derived default.
        correctAction: state.verifyCorrectAction || '',
        lecturerReviewRequired: Boolean(state.verifyLecturerReviewRequired),
      };
    }
    return {
      ...base,
      expectedAnswerSlots: state.expectedAnswerSlots
        .map((slot, idx) => {
          const answers = slot.acceptableAnswers
            .filter((a) => a.drug.trim())
            .map((a) => ({
              drug: a.drug.trim(),
              formulation: a.formulation.trim(),
              strength: a.strength.trim(),
              route: a.route.trim(),
              doseAmount: a.doseAmount === '' ? null : Number(a.doseAmount),
              doseUnit: a.doseUnit.trim(),
              frequency: a.frequency.trim(),
              durationDays: a.durationDays === '' ? null : Number(a.durationDays),
              durationDoses: a.durationDoses === '' ? null : Number(a.durationDoses),
              quantity: a.quantity === '' ? null : Number(a.quantity),
            }));
          if (answers.length === 0) return null;
          return {
            id: slot.id || `slot-${idx + 1}`,
            label: slot.label.trim(),
            controlledDrug: Boolean(slot.controlledDrug),
            cdSchedule: slot.controlledDrug && slot.cdSchedule ? Number(slot.cdSchedule) : null,
            instalmentScript: Boolean(slot.controlledDrug && slot.instalmentScript),
            counsellingFeedback: slot.counsellingFeedback,
            lecturerReviewRequired: Boolean(slot.lecturerReviewRequired),
            acceptableAnswers: answers,
          };
        })
        .filter(Boolean),
    };
  };

  const handleSave = () => {
    if (validation.length > 0) return;
    onSave?.({
      title: state.title.trim(),
      summary: state.summary.trim(),
      freeTier: state.freeTier,
      difficulty: state.difficulty,
      payload: buildPayload(),
    });
  };

  const handlePreview = () => {
    if (validation.length > 0 || !onPreview) return;
    onPreview({
      title: state.title.trim() || 'Untitled preview',
      payload: buildPayload(),
    });
  };

  if (loading) {
    return <div className="student-page"><Container className="mt-4"><Spinner animation="border" /> Loading case study…</Container></div>;
  }

  const datalistDoseUnitsId = 'outprx-dose-units';

  return (
    <div className="student-page">
      <Container className="mt-4 facilitator-editor">
        <datalist id={datalistDoseUnitsId}>
          {COMMON_DOSE_UNITS.map((u) => <option key={u} value={u} />)}
        </datalist>
        <div ref={saveBarRef} className={tutorial.isStepActive('save') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}>
          <FacilitatorPageHeader
            title={isEdit ? 'Edit outpatient case study' : 'New outpatient case study'}
            onBack={onCancel}
            backLabel="Cancel"
            actions={<>
              <Button type="button" variant="outline-secondary" size="sm" onClick={tutorial.start}>
                <i className="bi bi-question-circle me-1" /> Walkthrough
              </Button>
              {onPreview ? (
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  onClick={handlePreview}
                  disabled={saving || validation.length > 0}
                  title={validation.length > 0 ? 'Resolve the validation errors below first.' : 'Open this case study as a student would see it. Nothing is saved.'}
                >
                  <i className="bi bi-eye me-1" /> Preview as student
                </Button>
              ) : null}
              <Button type="button" variant="primary" size="sm" onClick={handleSave} disabled={saving || validation.length > 0}>
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create draft'}
              </Button>
            </>}
          />
        </div>

        {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}
        {validation.length > 0 ? (
          <Alert variant="warning">
            <strong>Before saving:</strong>
            <ul className="mb-0">{validation.map((e, i) => <li key={i}>{e}</li>)}</ul>
          </Alert>
        ) : null}

        <EditorHelpPanel title="How to author an outpatient case study (click to expand)" defaultOpen={false}>
          <p><strong>Training simulation only.</strong> The student form always carries a <em>TRAINING SIMULATION - NOT A VALID PRESCRIPTION</em> banner and uses a fixed simulation prescriber stamp; students cannot produce a document that looks like a real signed prescription. The submission record carries the student&apos;s name and timestamp so you can see whose work it is when reviewing.</p>
          <p>Two modes share the same module:</p>
          <ul>
            <li><strong>Prescribe</strong> — you author the answer key (one or more drugs, with acceptable variants). The student writes the prescription on a simulated outpatient form and the engine grades against your answer key (precision + recall on drugs + legal CD checks).</li>
            <li><strong>Verify</strong> — you author the prescription itself (often deliberately flawed). The student reviews it, ticks every issue from the closed catalogue, and picks the right onward action. Engine grades on flag precision + recall + action correctness.</li>
          </ul>
          <p>The Mode toggle is in the basics card. Switching mode keeps your title, patient and scenario; the prescription / answer-key part is mode-specific.</p>
          <ol>
            <li><strong>Case study basics</strong> — title, summary, difficulty, free-tier toggle, mode.</li>
            <li><strong>Patient details</strong> — use Random Male / Female to fill demographics from the curated pool.</li>
            <li><strong>Clinical scenario</strong> — a short paragraph stating what the prescription is for and any relevant clinical context (renal function, allergies, recent treatment).</li>
            <li><strong>In Prescribe mode</strong> — for each drug the student must include, add an entry with one or more acceptable answers. Type 2+ characters in the drug field to search the library; strength, formulation, route and frequency auto-fill. Picking a CD from the library auto-ticks the entry&apos;s CD toggle.</li>
            <li><strong>In Verify mode</strong> — author the prescription you want the student to review (often deliberately flawed). Then pick the issues from the catalogue: <em>clinical</em> (would harm), <em>legal</em> (would not be dispensed), <em>best practice</em> (safe but suboptimal). Set the correct onward action; if you don&apos;t, we derive it from the highest-severity issue.</li>
            <li><strong>The student is not told</strong> how many drugs to prescribe / which issues are present — that&apos;s the assessment.</li>
          </ol>
        </EditorHelpPanel>

        <div ref={basicsRef} className={`card mb-3 ${tutorial.isStepActive('basics') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}`}>
          <div className="card-body">
            <h5>Case study basics</h5>
            <Row className="g-2 align-items-end">
              <Col md={6}><Form.Group><Form.Label>Title</Form.Label><Form.Control value={state.title} onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))} /></Form.Group></Col>
              <Col md={3}><Form.Group><Form.Label>Difficulty</Form.Label>
                <Form.Select value={state.difficulty} onChange={(e) => setState((s) => ({ ...s, difficulty: e.target.value }))}>
                  {DIFFICULTIES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </Form.Select>
              </Form.Group></Col>
              <Col md={3} className="d-flex align-items-end">
                <Form.Check type="switch" id="outprx-freetier" label="Free tier" checked={state.freeTier} onChange={(e) => setState((s) => ({ ...s, freeTier: e.target.checked }))} />
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Mode</Form.Label>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant={state.mode === 'prescribe' ? 'primary' : 'outline-secondary'}
                      onClick={() => setMode('prescribe')}
                      type="button"
                    >
                      <i className="bi bi-pencil-square me-1" /> Prescribe
                    </Button>
                    <Button
                      size="sm"
                      variant={state.mode === 'verify' ? 'primary' : 'outline-secondary'}
                      onClick={() => setMode('verify')}
                      type="button"
                    >
                      <i className="bi bi-search me-1" /> Verify
                    </Button>
                    <span className="small text-muted ms-2 align-self-center">
                      Select an education mode: is the student writing a prescription, or are they verifying one?
                    </span>
                  </div>
                </Form.Group>
              </Col>
              <Col xs={12}><Form.Group><Form.Label>Summary</Form.Label><Form.Control as="textarea" rows={2} value={state.summary} onChange={(e) => setState((s) => ({ ...s, summary: e.target.value }))} /></Form.Group></Col>
            </Row>
          </div>
        </div>

        <div ref={patientRef} className={`card mb-3 ${tutorial.isStepActive('patient') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}`}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
              <h5 className="mb-0">Patient details</h5>
              <div className="d-flex gap-2">
                <Button size="sm" variant="outline-primary" onClick={() => generatePatient('male_names')}>Random Male Patient</Button>
                <Button size="sm" variant="outline-primary" onClick={() => generatePatient('female_names')}>Random Female Patient</Button>
              </div>
            </div>
            <Row className="g-2">
              <Col md={6}><Form.Group><Form.Label>Patient name</Form.Label><Form.Control value={state.patient.name} onChange={(e) => updatePatient('name', e.target.value)} /></Form.Group></Col>
              <Col md={3}><Form.Group><Form.Label>Date of birth</Form.Label><Form.Control type="date" value={state.patient.dob} onChange={(e) => updatePatient('dob', e.target.value)} /></Form.Group></Col>
              <Col md={3}><Form.Group><Form.Label>Allergies</Form.Label><Form.Control value={state.patient.allergies} onChange={(e) => updatePatient('allergies', e.target.value)} placeholder="NKDA" /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Training NHS number</Form.Label><Form.Control value={state.patient.trainingNhsNumber} onChange={(e) => updatePatient('trainingNhsNumber', e.target.value)} placeholder="TRAINING-1234567890" /></Form.Group></Col>
              <Col md={8}><Form.Group><Form.Label>Training address</Form.Label><Form.Control value={state.patient.trainingAddress} onChange={(e) => updatePatient('trainingAddress', e.target.value)} placeholder="Training Scenario - Not a Real Address" /></Form.Group></Col>
            </Row>
          </div>
        </div>

        <div ref={scenarioRef} className={`card mb-3 ${tutorial.isStepActive('scenario') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}`}>
          <div className="card-body">
            <h5>Clinical scenario</h5>
            <p className="text-muted small mb-2">A short paragraph the student reads before writing the prescription. State what is being prescribed for, what has just happened, and any relevant clinical context (renal function, allergies, recent treatment).</p>
            <Form.Group>
              <Form.Control as="textarea" rows={4} value={state.scenario} onChange={(e) => setState((s) => ({ ...s, scenario: e.target.value }))} />
            </Form.Group>
          </div>
        </div>

        {state.mode === 'prescribe' ? (
        <div ref={slotsRef} className={`card mb-3 ${tutorial.isStepActive('slots') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}`}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Expected prescription</h5>
              <Button size="sm" variant="outline-secondary" onClick={addSlot}>+ Add prescription</Button>
            </div>
            <p className="text-muted small mb-3">
              {libraryLoaded
                ? <>Type at least 2 characters in the drug field to search the library; click a result to autopopulate strength, formulation, route, dose unit and frequency.</>
                : <>Loading drug library…</>}
            </p>
            {state.expectedAnswerSlots.map((slot, slotIdx) => (
              <div key={slot.id || slotIdx} className="border rounded p-3 mb-3" style={{ backgroundColor: slot.controlledDrug ? '#fff8e1' : 'transparent' }}>
                <Row className="g-2 align-items-end mb-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="small mb-1">Description (e.g. &quot;First-line antibiotic for UTI&quot;)</Form.Label>
                      <Form.Control size="sm" value={slot.label} onChange={(e) => updateSlot(slotIdx, 'label', e.target.value)} />
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <Form.Check type="switch" id={`outprx-cd-slot-${slotIdx}`} label="Controlled drug" checked={slot.controlledDrug} onChange={(e) => updateSlot(slotIdx, 'controlledDrug', e.target.checked)} />
                  </Col>
                  <Col md={1} className="d-flex align-items-end justify-content-end">
                    {state.expectedAnswerSlots.length > 1 ? <Button size="sm" variant="outline-danger" onClick={() => removeSlot(slotIdx)} title="Remove this prescription">×</Button> : null}
                  </Col>
                </Row>
                {slot.controlledDrug ? (
                  <Row className="g-2 align-items-end mb-3">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small mb-1">CD schedule</Form.Label>
                        <Form.Select size="sm" value={slot.cdSchedule} onChange={(e) => updateSlot(slotIdx, 'cdSchedule', e.target.value)}>
                          {CD_SCHEDULES.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                      <Form.Check type="switch" id={`outprx-instalment-${slotIdx}`} label="Instalment script" checked={slot.instalmentScript} onChange={(e) => updateSlot(slotIdx, 'instalmentScript', e.target.checked)} />
                    </Col>
                  </Row>
                ) : null}
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <strong className="small">Acceptable answers</strong>
                    <Button size="sm" variant="outline-secondary" onClick={() => addAnswer(slotIdx)}>+ Add answer</Button>
                  </div>
                  <p className="text-muted small mb-2">Each answer is a fully-specified prescription. The student matches if their drug equals any of the acceptable answers.</p>
                </div>
                {slot.acceptableAnswers.map((a, ansIdx) => (
                  <div key={ansIdx} className="border rounded p-2 mb-2" style={{ backgroundColor: '#fafafa' }}>
                    <Row className="g-2 align-items-end mb-2">
                      <Col md={5}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Drug (search the library)</Form.Label>
                          <DrugAutocomplete
                            value={a.drug}
                            onChange={(v) => updateAnswer(slotIdx, ansIdx, 'drug', v)}
                            onPick={(item) => pickDrug(slotIdx, ansIdx, item)}
                            library={drugLibrary}
                            disabled={!libraryLoaded}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}><Form.Group><Form.Label className="small mb-1">Formulation</Form.Label><Form.Control size="sm" value={a.formulation} onChange={(e) => updateAnswer(slotIdx, ansIdx, 'formulation', e.target.value)} placeholder="Tablet" /></Form.Group></Col>
                      <Col md={3}><Form.Group><Form.Label className="small mb-1">Strength</Form.Label><Form.Control size="sm" value={a.strength} onChange={(e) => updateAnswer(slotIdx, ansIdx, 'strength', e.target.value)} placeholder="200mg" /></Form.Group></Col>
                      <Col md={1} className="d-flex align-items-end justify-content-end">
                        {slot.acceptableAnswers.length > 1 ? <Button size="sm" variant="outline-danger" onClick={() => removeAnswer(slotIdx, ansIdx)} title="Remove answer">×</Button> : null}
                      </Col>
                    </Row>
                    <Row className="g-2 mb-2">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Route</Form.Label>
                          <Form.Select size="sm" value={a.route} onChange={(e) => updateAnswer(slotIdx, ansIdx, 'route', e.target.value)}>
                            <option value="">Select route</option>
                            {routeOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Dose amount</Form.Label>
                          <Form.Control size="sm" type="number" min="0" step="any" value={a.doseAmount} onChange={(e) => updateAnswer(slotIdx, ansIdx, 'doseAmount', e.target.value)} placeholder="1" />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Dose unit</Form.Label>
                          <Form.Control size="sm" list={datalistDoseUnitsId} value={a.doseUnit} onChange={(e) => updateAnswer(slotIdx, ansIdx, 'doseUnit', e.target.value)} placeholder="tablet" />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Frequency</Form.Label>
                          <Form.Select size="sm" value={a.frequency} onChange={(e) => updateAnswer(slotIdx, ansIdx, 'frequency', e.target.value)}>
                            <option value="">Select frequency</option>
                            {frequencyOptions.map((f) => <option key={f} value={f}>{f}</option>)}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="g-2">
                      <Col md={3}><Form.Group><Form.Label className="small mb-1">Duration (days)</Form.Label><Form.Control size="sm" type="number" min="0" value={a.durationDays} onChange={(e) => updateAnswer(slotIdx, ansIdx, 'durationDays', e.target.value)} /></Form.Group></Col>
                      <Col md={3}><Form.Group><Form.Label className="small mb-1">Duration (doses)</Form.Label><Form.Control size="sm" type="number" min="0" value={a.durationDoses} onChange={(e) => updateAnswer(slotIdx, ansIdx, 'durationDoses', e.target.value)} /></Form.Group></Col>
                      <Col md={3}><Form.Group><Form.Label className="small mb-1">Quantity</Form.Label><Form.Control size="sm" type="number" min="0" value={a.quantity} onChange={(e) => updateAnswer(slotIdx, ansIdx, 'quantity', e.target.value)} /></Form.Group></Col>
                      <Col md={3} className="text-muted small d-flex align-items-end pb-1">
                        Leave fields blank to skip grading them.
                      </Col>
                    </Row>
                  </div>
                ))}
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-1">Counselling feedback (optional, shown to the student after they submit)</Form.Label>
                  <Form.Control as="textarea" rows={2} size="sm" value={slot.counsellingFeedback} onChange={(e) => updateSlot(slotIdx, 'counsellingFeedback', e.target.value)} placeholder="e.g. Counsel the patient on adequate fluid intake and completing the full course." />
                </Form.Group>
                <Form.Check type="switch" id={`outprx-lecturer-${slotIdx}`} label="Also flag this submission for lecturer review" checked={slot.lecturerReviewRequired} onChange={(e) => updateSlot(slotIdx, 'lecturerReviewRequired', e.target.checked)} />
              </div>
            ))}
          </div>
        </div>
        ) : null}

        {state.mode === 'verify' ? (
        <>
          <div ref={slotsRef} className={`card mb-3 ${tutorial.isStepActive('slots') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}`}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Prescription to review</h5>
                <Button size="sm" variant="outline-secondary" onClick={addVerifyDrug}>+ Add drug</Button>
              </div>
              <p className="text-muted small mb-3">
                Author the prescription exactly as it would appear on the printed script - including the wrong values that the student needs to spot. Pick drugs from the library; you can deliberately type non-library values where the error is in the data the prescriber wrote.
              </p>
              {state.verifyPrescription.drugs.map((d, idx) => (
                <div key={idx} className="border rounded p-2 mb-2" style={{ backgroundColor: d.isControlledDrug ? '#fff8e1' : '#fafafa' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong className="small">Drug {idx + 1}{d.isControlledDrug ? ' (controlled drug)' : ''}</strong>
                    {state.verifyPrescription.drugs.length > 1
                      ? <Button size="sm" variant="outline-danger" onClick={() => removeVerifyDrug(idx)} title="Remove this drug">×</Button>
                      : null}
                  </div>
                  <Row className="g-2 align-items-end mb-2">
                    <Col md={5}>
                      <Form.Group>
                        <Form.Label className="small mb-1">Drug</Form.Label>
                        <DrugAutocomplete
                          value={d.drug}
                          onChange={(v) => updateVerifyDrug(idx, 'drug', v)}
                          onPick={(item) => pickVerifyDrug(idx, item)}
                          library={drugLibrary}
                          disabled={!libraryLoaded}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}><Form.Group><Form.Label className="small mb-1">Formulation</Form.Label><Form.Control size="sm" value={d.formulation} onChange={(e) => updateVerifyDrug(idx, 'formulation', e.target.value)} /></Form.Group></Col>
                    <Col md={3}><Form.Group><Form.Label className="small mb-1">Strength</Form.Label><Form.Control size="sm" value={d.strength} onChange={(e) => updateVerifyDrug(idx, 'strength', e.target.value)} /></Form.Group></Col>
                  </Row>
                  <Row className="g-2 mb-2">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small mb-1">Route</Form.Label>
                        <Form.Select size="sm" value={d.route} onChange={(e) => updateVerifyDrug(idx, 'route', e.target.value)}>
                          <option value="">Select route</option>
                          {routeOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}><Form.Group><Form.Label className="small mb-1">Dose amount</Form.Label><Form.Control size="sm" type="number" min="0" step="any" value={d.doseAmount} onChange={(e) => updateVerifyDrug(idx, 'doseAmount', e.target.value)} /></Form.Group></Col>
                    <Col md={3}><Form.Group><Form.Label className="small mb-1">Dose unit</Form.Label><Form.Control size="sm" list={datalistDoseUnitsId} value={d.doseUnit} onChange={(e) => updateVerifyDrug(idx, 'doseUnit', e.target.value)} /></Form.Group></Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small mb-1">Frequency</Form.Label>
                        <Form.Select size="sm" value={d.frequency} onChange={(e) => updateVerifyDrug(idx, 'frequency', e.target.value)}>
                          <option value="">Select frequency</option>
                          {frequencyOptions.map((f) => <option key={f} value={f}>{f}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="g-2 mb-2">
                    <Col md={3}><Form.Group><Form.Label className="small mb-1">Duration (days)</Form.Label><Form.Control size="sm" type="number" min="0" value={d.durationDays} onChange={(e) => updateVerifyDrug(idx, 'durationDays', e.target.value)} /></Form.Group></Col>
                    <Col md={3}><Form.Group><Form.Label className="small mb-1">Duration (doses)</Form.Label><Form.Control size="sm" type="number" min="0" value={d.durationDoses} onChange={(e) => updateVerifyDrug(idx, 'durationDoses', e.target.value)} /></Form.Group></Col>
                    <Col md={3}><Form.Group><Form.Label className="small mb-1">Quantity (figures)</Form.Label><Form.Control size="sm" type="number" min="0" value={d.quantity} onChange={(e) => updateVerifyDrug(idx, 'quantity', e.target.value)} /></Form.Group></Col>
                    <Col md={3} className="d-flex align-items-end">
                      <Form.Check type="switch" id={`outprx-vcd-${idx}`} label="Controlled drug" checked={d.isControlledDrug} onChange={(e) => updateVerifyDrug(idx, 'isControlledDrug', e.target.checked)} />
                    </Col>
                  </Row>
                  {d.isControlledDrug ? (
                    <Row className="g-2">
                      <Col md={6}><Form.Group><Form.Label className="small mb-1">Quantity in words (often the source of the legal error)</Form.Label><Form.Control size="sm" value={d.quantityInWords} onChange={(e) => updateVerifyDrug(idx, 'quantityInWords', e.target.value)} placeholder="Leave blank to author a missing-quantity-in-words error" /></Form.Group></Col>
                    </Row>
                  ) : null}
                </div>
              ))}

              <h6 className="mt-4 mb-2">Form metadata (the lecturer-authored prescription)</h6>
              <Row className="g-2 mb-2">
                <Col md={4}><Form.Group><Form.Label className="small mb-1">Prescription date</Form.Label><Form.Control size="sm" type="date" value={state.verifyPrescription.formMeta.prescriptionDate} onChange={(e) => setState((s) => ({ ...s, verifyPrescription: { ...s.verifyPrescription, formMeta: { ...s.verifyPrescription.formMeta, prescriptionDate: e.target.value } } }))} /></Form.Group></Col>
                <Col md={4}><Form.Group><Form.Label className="small mb-1">Prescriber address</Form.Label><Form.Control size="sm" value={state.verifyPrescription.formMeta.prescriberAddress} onChange={(e) => setState((s) => ({ ...s, verifyPrescription: { ...s.verifyPrescription, formMeta: { ...s.verifyPrescription.formMeta, prescriberAddress: e.target.value } } }))} /></Form.Group></Col>
                <Col md={4} className="d-flex align-items-end">
                  <Form.Check type="switch" id="outprx-vsig-present" label="Signature present" checked={state.verifyPrescription.formMeta.signaturePresent} onChange={(e) => setState((s) => ({ ...s, verifyPrescription: { ...s.verifyPrescription, formMeta: { ...s.verifyPrescription.formMeta, signaturePresent: e.target.checked } } }))} />
                </Col>
              </Row>
              <p className="text-muted small mb-0">Leave a field empty / signature off to author a corresponding whole-form error in the catalogue below.</p>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Errors in this prescription</h5>
                <Button size="sm" variant="outline-secondary" onClick={addVerifyError}>+ Add error</Button>
              </div>
              <p className="text-muted small mb-3">
                Tick every error the student should flag. Leave the list empty if this is a correct prescription - the student should pick "Sign off as safe". Per-drug errors need a target drug; whole-form errors apply to the script overall.
              </p>
              {state.verifyErrors.length === 0 ? (
                <Alert variant="info" className="mb-0">No errors authored. The student should pick <strong>Sign off as safe</strong>; flagging anything will cost precision.</Alert>
              ) : null}
              {state.verifyErrors.map((err, idx) => {
                const cat = err.key ? VERIFY_ERROR_BY_KEY[err.key] : null;
                const drugOptions = state.verifyPrescription.drugs.map((d) => d.drug.trim()).filter(Boolean);
                return (
                  <div key={idx} className="border rounded p-2 mb-2" style={{ backgroundColor: '#fafafa' }}>
                    <Row className="g-2 align-items-end mb-2">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Error type</Form.Label>
                          <Form.Select size="sm" value={err.key} onChange={(e) => updateVerifyError(idx, 'key', e.target.value)}>
                            <option value="">Pick from the catalogue</option>
                            <optgroup label="Clinical (would harm)">
                              {VERIFY_ERROR_TYPES.filter((t) => t.severity === 'clinical').map((t) => (
                                <option key={t.key} value={t.key}>{t.label}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Legal (would not be dispensed)">
                              {VERIFY_ERROR_TYPES.filter((t) => t.severity === 'legal').map((t) => (
                                <option key={t.key} value={t.key}>{t.label}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Best practice (safe but suboptimal)">
                              {VERIFY_ERROR_TYPES.filter((t) => t.severity === 'best_practice').map((t) => (
                                <option key={t.key} value={t.key}>{t.label}</option>
                              ))}
                            </optgroup>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={5}>
                        {cat?.appliesTo === 'per_drug' ? (
                          <Form.Group>
                            <Form.Label className="small mb-1">Applies to drug</Form.Label>
                            <Form.Select size="sm" value={err.drug} onChange={(e) => updateVerifyError(idx, 'drug', e.target.value)}>
                              <option value="">Pick which drug</option>
                              {drugOptions.map((dn) => <option key={dn} value={dn}>{dn}</option>)}
                            </Form.Select>
                          </Form.Group>
                        ) : cat ? (
                          <div className="small text-muted pb-1">Applies to the whole prescription</div>
                        ) : null}
                      </Col>
                      <Col md={1} className="d-flex align-items-end justify-content-end">
                        <Button size="sm" variant="outline-danger" onClick={() => removeVerifyError(idx)} title="Remove this error">×</Button>
                      </Col>
                    </Row>
                    <Form.Group>
                      <Form.Label className="small mb-1">Lecturer note (private - shown only after the student submits, with the answer key)</Form.Label>
                      <Form.Control as="textarea" rows={2} size="sm" value={err.note} onChange={(e) => updateVerifyError(idx, 'note', e.target.value)} placeholder="e.g. BNFC dose for 1-4 yrs is 250mg TDS, not 500mg" />
                    </Form.Group>
                    {cat ? (
                      <div className="mt-2">
                        <Badge {...(SEVERITY_BADGE_VARIANT[cat.severity] || { bg: 'secondary' })}>
                          {cat.severity === 'clinical' ? 'Clinical (would harm)'
                            : cat.severity === 'legal' ? 'Legal (would not be dispensed)'
                            : 'Best practice (safe but suboptimal)'}
                        </Badge>
                      </div>
                    ) : null}
                  </div>
                );
              })}

              <hr className="my-3" />
              <h6>Correct onward action</h6>
              <p className="text-muted small mb-2">What should the student do with this prescription? Pick a specific action, or leave on auto to use the severity-based default ({(() => {
                const auto = deriveDefaultVerifyAction(state.verifyErrors);
                return VERIFY_ACTIONS.find((a) => a.key === auto)?.label || auto;
              })()}).</p>
              <Form.Select
                size="sm"
                value={state.verifyCorrectAction}
                onChange={(e) => setState((s) => ({ ...s, verifyCorrectAction: e.target.value }))}
              >
                <option value="">Auto (severity-derived)</option>
                {VERIFY_ACTIONS.map((a) => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
              </Form.Select>

              <Form.Check
                className="mt-3"
                type="switch"
                id="outprx-verify-lec-review"
                label="Flag every submission for lecturer review (in addition to auto-grading)"
                checked={state.verifyLecturerReviewRequired}
                onChange={(e) => setState((s) => ({ ...s, verifyLecturerReviewRequired: e.target.checked }))}
              />
            </div>
          </div>
        </>
        ) : null}

        <div className="card mb-3">
          <div className="card-body">
            <h5>Exercise-level counselling feedback (optional)</h5>
            <p className="text-muted small mb-2">Free-text feedback shown to the student after they submit, alongside the per-prescription counselling notes. Use for general advice that applies across the whole case study (e.g. &quot;Always check the patient understands before they leave the consultation&quot;). One point per line.</p>
            <Form.Control as="textarea" rows={3} value={state.customMustHaves} onChange={(e) => setState((s) => ({ ...s, customMustHaves: e.target.value }))} placeholder="One point per line" />
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mb-4">
          <Button type="button" variant="outline-secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
          {onPreview ? (
            <Button
              type="button"
              variant="outline-primary"
              onClick={handlePreview}
              disabled={saving || validation.length > 0}
            >
              <i className="bi bi-eye me-1" /> Preview as student
            </Button>
          ) : null}
          <Button type="button" variant="primary" onClick={handleSave} disabled={saving || validation.length > 0}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create draft'}
          </Button>
        </div>
      </Container>
      <GuidedTutorialOverlay tutorial={tutorial} titleId="outpatientEditorTutorialTitle" />
    </div>
  );
};

export default FacilitatorOutpatientEditor;
